import React from 'react';

export function PrimaryButton({ children, disabled = false, size = 'medium', className = '', ...props }) {
  const sizeClasses = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg'
  };

  const classes = `
    bg-white text-purple-700 font-semibold rounded-lg shadow-md 
    hover:bg-purple-50 hover:text-purple-800 
    active:bg-purple-100
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50
    ${sizeClasses[size]}
    ${className}
  `;

  return (
    <button
      className={classes}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, disabled = false, size = 'medium', className = '', ...props }) {
  const sizeClasses = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg'
  };

  const classes = `
    border border-white border-opacity-50 text-white font-medium rounded-lg
    hover:bg-white hover:bg-opacity-10 hover:border-opacity-80
    active:bg-white active:bg-opacity-20
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-30
    ${sizeClasses[size]}
    ${className}
  `;

  return (
    <button
      className={classes}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

export function IconButton({ 
  children, 
  disabled = false, 
  size = 'medium',
  variant = 'default', // 'default', 'danger'
  className = '', 
  ...props 
}) {
  const sizeClasses = {
    small: 'w-8 h-8 text-sm',
    medium: 'w-10 h-10 text-base',
    large: 'w-12 h-12 text-lg'
  };

  const variantClasses = {
    default: `
      text-gray-400 hover:text-gray-200 hover:bg-white hover:bg-opacity-10
      focus:ring-white focus:ring-opacity-30
    `,
    danger: `
      text-red-400 hover:text-red-300 hover:bg-red-400 hover:bg-opacity-10
      focus:ring-red-400 focus:ring-opacity-30
    `
  };

  const classes = `
    flex items-center justify-center rounded-lg
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  return (
    <button
      className={classes}
      disabled={disabled}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
}

export function Button({ 
  children, 
  variant = 'contained', // 'contained', 'outlined', 'text'
  color = 'primary', // 'primary', 'secondary', 'error'
  size = 'medium',
  disabled = false,
  fullWidth = false,
  className = '',
  ...props 
}) {
  const sizeClasses = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg'
  };

  const getVariantClasses = () => {
    const variants = {
      contained: {
        primary: 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-400',
        secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-400',
        error: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-400'
      },
      outlined: {
        primary: 'border border-purple-400 text-purple-400 hover:bg-purple-400 hover:bg-opacity-10 focus:ring-purple-400',
        secondary: 'border border-gray-400 text-gray-400 hover:bg-gray-400 hover:bg-opacity-10 focus:ring-gray-400',
        error: 'border border-red-400 text-red-400 hover:bg-red-400 hover:bg-opacity-10 focus:ring-red-400'
      },
      text: {
        primary: 'text-purple-400 hover:bg-purple-400 hover:bg-opacity-10 focus:ring-purple-400',
        secondary: 'text-gray-400 hover:bg-gray-400 hover:bg-opacity-10 focus:ring-gray-400',
        error: 'text-red-400 hover:bg-red-400 hover:bg-opacity-10 focus:ring-red-400'
      }
    };
    
    return variants[variant][color];
  };

  const classes = `
    font-medium rounded-lg transition-all duration-200 ease-in-out
    disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-opacity-50
    ${sizeClasses[size]}
    ${getVariantClasses()}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;

  return (
    <button
      className={classes}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
} 